import styles from "./styles.module.scss"
import { observer } from "mobx-react-lite"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"
import { getLocationsText, SearchDestination, SearchOrigin } from "@cruisen/react-shared"
import FormTextInput from "../../atoms/FormTextInput"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { findPorts } from "../../../services/firebase-functions/ports"
import { transformDeparturePortGroups, transformDestinationRegions } from "../../../utils/ports"
import { MonthPicker } from "../../atoms/MonthPicker"
import { FormMultiSelectInput } from "../../atoms/FormMultiSelectInput"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"
import { useEffect } from "react"
import { colors } from "@theme/colors"
import CloseModalButton from "../../buttons/CloseModalButton"

export type CreateTripFormValues = {
  tripName: string
  originPortGroups: SearchOrigin[]
  destinationRegions: SearchDestination[]
  months: Date[]
}

const createTripSchema = yup.object().shape({
  tripName: yup.string().required("-"),
  originPortGroups: yup.array(),
  destinationRegions: yup.array(),
  months: yup.array().min(1, "-"),
})

export const TripDetailsModal = observer(
  ({
    closeModal,
    onSubmit,
    defaultValues,
    isLoading = false,
  }: {
    closeModal: () => void
    onSubmit: (values: CreateTripFormValues) => void
    defaultValues?: CreateTripFormValues
    isLoading?: boolean
  }) => {
    const {
      control,
      formState: { isValid },
      watch,
      reset,
      setValue,
      handleSubmit,
      trigger,
    } = useForm({
      defaultValues: {
        tripName: defaultValues?.tripName || "",
        originPortGroups: defaultValues?.originPortGroups || [],
        destinationRegions: defaultValues?.destinationRegions || [],
        months: defaultValues?.months || [],
      },
      resolver: yupResolver(createTripSchema),
    })

    const [originPortGroups, destinationRegions, months] = watch([
      "originPortGroups",
      "destinationRegions",
      "months",
    ])

    const atLeastOneLocation = originPortGroups.length > 0 || destinationRegions.length > 0
    const departurePortGroupIds = originPortGroups.map((portGroup) => Number(portGroup.id))
    const destinationRegionIds = destinationRegions.map((region) => Number(region.id))
    const tripMonths = months.map((e) => e.getTime())

    const { data: filteredPortsData, isLoading: arePortsLoading } = useQuery({
      queryKey: [
        "filteredPorts",
        departurePortGroupIds.toString(),
        destinationRegionIds.toString(),
        tripMonths.toString(),
      ],
      queryFn: () =>
        findPorts({
          departurePortGroupIds,
          destinationRegionIds,
          tripMonths,
        }),
      placeholderData: keepPreviousData,
    })

    useEffect(() => {
      // If no sailings are found reset search criteria
      if (filteredPortsData && filteredPortsData?.sailingCount === 0) {
        reset({
          originPortGroups: [],
          destinationRegions: [],
          months: [],
        })
      }
    }, [filteredPortsData])

    const availableDates = (filteredPortsData?.months || []).map((availableDate) => {
      const [year, month, day] = availableDate.split("-").map((text) => Number(text))
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0))
    })

    return (
      <div className={styles.container} data-testid="trip-details-modal">
        <div className={styles.header}>
          <div>
            <h2>{defaultValues ? "Edit trip" : "Add new trip"}</h2>
          </div>
          <CloseModalButton onClick={closeModal} />
        </div>
        <div>
          <p className={styles.sectionTitle}>Trip details</p>
          <FormTextInput
            control={control}
            autoFocus
            name="tripName"
            label="Trip name*"
            testID="trip-name-input"
            parentBackgroundColor={colors.backgroundSecondary}
          />
          <div className={styles.row}>
            <FormMultiSelectInput
              name="originPortGroups"
              control={control}
              label="Sailing from"
              searchLabel="Search port group"
              textValue={
                originPortGroups.length > 0
                  ? getLocationsText(originPortGroups.map(({ name }) => name))
                  : ""
              }
              options={transformDeparturePortGroups(filteredPortsData?.departurePortGroups || [])}
              handleChange={() => trigger()}
              isLoading={arePortsLoading}
              testID="origin-port-groups-input"
              parentBackgroundColor={colors.backgroundSecondary}
            />
            <FormMultiSelectInput
              name="destinationRegions"
              control={control}
              label="Sailing to"
              searchLabel="Search region"
              textValue={
                destinationRegions.length > 0
                  ? getLocationsText(destinationRegions.map(({ name }) => name))
                  : ""
              }
              options={transformDestinationRegions(filteredPortsData?.destinationRegions || [])}
              handleChange={() => trigger()}
              isLoading={arePortsLoading}
              testID="destination-regions-input"
              parentBackgroundColor={colors.backgroundSecondary}
            />
          </div>
        </div>
        <div>
          <div className={styles.datesHeader}>
            <p className={styles.datesText}>Dates*</p>
            {arePortsLoading && <DefaultSpinner size="sm" />}
          </div>
          <MonthPicker
            availableMonths={availableDates}
            isLoading={arePortsLoading}
            selectedMonths={months}
            onChange={(selectedDates) => {
              setValue("months", selectedDates)
              trigger()
            }}
          />
        </div>
        <button
          type="button"
          disabled={arePortsLoading || isLoading || !isValid || !atLeastOneLocation}
          className={styles.submitButton}
          onClick={handleSubmit(onSubmit)}
          data-testid="submit-button"
        >
          {defaultValues
            ? isLoading
              ? "Saving..."
              : "Save"
            : isLoading
              ? "Creating trip..."
              : "Create trip"}
        </button>
      </div>
    )
  },
)
